import React, { useEffect, useRef, useState } from "react";

/**
 * Full-viewport interactive 3D Bliencer mark, rendered as a monochrome
 * point-cloud sampled from `/brand/bliencer-mark.png`.
 *
 * Design goals (from user brief):
 *  • Black background, pure white particles — no colors.
 *  • Mouse-driven parallax rotation with smooth damping.
 *  • Cinematic reveal: fade + scale 0.95 → 1 + subtle rotation.
 *  • Lazy: THREE only imported once the section enters the viewport.
 *  • Respects prefers-reduced-motion — renders a static point cloud
 *    with mouse interaction disabled.
 *
 * Uses three.js Points (no PBR meshes) because the reference aesthetic
 * the user shared is a particle/pointillism field, not a solid shape.
 */
export default function Logo3D({ background = false }) {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const [active, setActive] = useState(false);

  // In background mode the mark sits behind CTA copy — dial it right
  // down so it reads as ambient texture, never fighting the headline.
  const baseCoreOpacity = background ? 0.14 : 1.0;
  const baseGlowOpacity = background ? 0.006 : 0.03;
  // Camera Z — background mode pulls back so the mark occupies less of
  // the section and reads as art rather than a subject.
  const cameraZ = background ? 12 : 8;

  // Lazy trigger — only kick off THREE init once the section is visible.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.25) {
            setActive(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: [0.25] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    let cleanup = () => {};
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    (async () => {
      const THREE = await import("three");
      if (disposed) return;

      // ------- 1. Sample the logo PNG into a point-cloud ------- //
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = "/brand/bliencer-mark.png";
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
      });
      if (disposed) return;

      // Rasterize the mark into a hidden canvas so we can inspect alpha.
      const W = 220;
      const H = Math.round((img.height / img.width) * W);
      const c2d = document.createElement("canvas");
      c2d.width = W;
      c2d.height = H;
      const ctx = c2d.getContext("2d");
      ctx.drawImage(img, 0, 0, W, H);
      const data = ctx.getImageData(0, 0, W, H).data;

      // Every opaque pixel becomes a particle. step=2 keeps enough
      // spacing between points that they read as individual dots
      // forming the mark, not a filled-in silhouette.
      const step = 2;
      const positions = [];
      const scale = 5.0 / W; // fits the mark into ~5 world units wide
      for (let y = 0; y < H; y += step) {
        for (let x = 0; x < W; x += step) {
          const a = data[(y * W + x) * 4 + 3];
          if (a > 140) {
            // Center around origin; flip Y (canvas Y grows downward).
            const px = (x - W / 2) * scale;
            const py = -(y - H / 2) * scale;
            // Small random Z-jitter so the cloud has visible depth when
            // the user rotates it — but not so much that the shape blurs.
            const pz = (Math.random() - 0.5) * 0.28;
            positions.push(px, py, pz);
          }
        }
      }

      // ------- 2. Build the THREE scene ------- //
      const scene = new THREE.Scene();
      scene.background = null; // parent div paints black

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
      });
      renderer.setPixelRatio(dpr);
      const resize = () => {
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        renderer.setSize(w, h, false);
        camera.aspect = w / Math.max(1, h);
        camera.updateProjectionMatrix();
      };

      const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
      // Camera Z is mode-aware (see cameraZ above): background mode
      // sits further back so the mark reads as ambient art.
      camera.position.set(0, 0, cameraZ);

      // Small round sprite for each particle — gives a soft "dust" feel
      // and reads cleanly against black at any distance.
      const spriteCanvas = document.createElement("canvas");
      spriteCanvas.width = spriteCanvas.height = 64;
      const sc = spriteCanvas.getContext("2d");
      const grad = sc.createRadialGradient(32, 32, 0, 32, 32, 32);
      grad.addColorStop(0.0, "rgba(255,255,255,1)");
      grad.addColorStop(0.4, "rgba(255,255,255,0.85)");
      grad.addColorStop(1.0, "rgba(255,255,255,0)");
      sc.fillStyle = grad;
      sc.fillRect(0, 0, 64, 64);
      const sprite = new THREE.CanvasTexture(spriteCanvas);
      sprite.colorSpace = THREE.SRGBColorSpace;

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3),
      );

      const mat = new THREE.PointsMaterial({
        color: 0xffffff,
        // Original dotted look: individual particles read as points at
        // the current camera distance. Additive blending lets adjacent
        // dots stay visible rather than merging into a solid silhouette.
        size: 0.06,
        map: sprite,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
      });

      const points = new THREE.Points(geom, mat);
      scene.add(points);

      // Soft glow halo — very large sprite, very low per-particle opacity.
      // Additive stacking builds a wide, gentle radiance that reaches far
      // past the mark's silhouette without ever forming a hard edge.
      const glowMat = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 1.6,
        map: sprite,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        sizeAttenuation: true,
        opacity: 0.035,
      });
      const glow = new THREE.Points(geom, glowMat);
      scene.add(glow);

      resize();
      window.addEventListener("resize", resize);

      // ------- 3. Mouse-driven parallax rotation ------- //
      const targetRot = { x: 0, y: 0 };
      const currentRot = { x: 0, y: 0 };
      const onPointerMove = (evt) => {
        if (reduce) return;
        // In background mode the canvas has pointer-events:none so we
        // listen at window scope and project against the canvas rect.
        const rect = canvas.getBoundingClientRect();
        const nx = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
        const ny = ((evt.clientY - rect.top) / rect.height) * 2 - 1;
        targetRot.y = nx * 0.6; // yaw
        targetRot.x = -ny * 0.35; // pitch
      };
      const moveTarget = background ? window : canvas;
      moveTarget.addEventListener("pointermove", onPointerMove);

      // ------- 4. Cinematic reveal ------- //
      const start = performance.now();
      const revealMs = reduce ? 0 : 1200;
      // Slight ambient drift so it never feels frozen even without cursor.
      const ambient = { yaw: 0 };

      const tick = () => {
        if (disposed) return;
        const t = performance.now() - start;
        const p = revealMs ? Math.min(1, t / revealMs) : 1;
        const ease = 1 - Math.pow(1 - p, 3); // easeOutCubic

        // Damped lerp toward target rotation from cursor.
        currentRot.x += (targetRot.x - currentRot.x) * 0.055;
        currentRot.y += (targetRot.y - currentRot.y) * 0.055;

        if (!reduce) ambient.yaw += 0.0018;

        points.rotation.x = currentRot.x;
        points.rotation.y = currentRot.y + ambient.yaw;
        glow.rotation.copy(points.rotation);

        // Reveal transform
        const s = 0.95 + 0.05 * ease;
        points.scale.setScalar(s);
        glow.scale.setScalar(s);
        mat.opacity = baseCoreOpacity * ease;
        glowMat.opacity = baseGlowOpacity * ease;

        renderer.render(scene, camera);
        rafId = requestAnimationFrame(tick);
      };
      let rafId = requestAnimationFrame(tick);

      cleanup = () => {
        cancelAnimationFrame(rafId);
        canvas.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("resize", resize);
        geom.dispose();
        mat.dispose();
        glowMat.dispose();
        sprite.dispose();
        renderer.dispose();
      };
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, [active]);

  // In background mode we render as an absolute-positioned canvas so a
  // parent section can position/size us behind its foreground content.
  if (background) {
    return (
      <div
        ref={rootRef}
        className="absolute inset-0 overflow-hidden pointer-events-none"
        data-testid="logo-3d-background"
        aria-hidden
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full"
        />
      </div>
    );
  }

  return (
    <section
      ref={rootRef}
      className="relative w-full bg-black overflow-hidden"
      style={{ height: "100vh" }}
      data-testid="logo-3d-section"
      aria-label="Bliencer 3D mark"
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        style={{ touchAction: "none" }}
      />
      <div
        className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.35em] text-white/40"
        aria-hidden
      >
        Move your cursor
      </div>
    </section>
  );
}
